var __defProp = Object.defineProperty;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __publicField = (obj, key, value) => {
  __defNormalProp(obj, typeof key !== "symbol" ? key + "" : key, value);
  return value;
};
import { localStorage } from "@play-co/browser-storage";
import * as userAgent from "@play-co/user-agent";
import amplitude from "amplitude-js";
import { pageWasReloaded } from "../browserUtils.mjs";
import { duration } from "../dateUtils.mjs";
import getQueryParameters from "../getQueryParameters.mjs";
import globalTime from "../globalTime.mjs";
import { LineConversionAPI } from "../line/LineConversionAPI.mjs";
import GCInstantVersion from "../packageVersion.mjs";
import { trackVisibilityChange } from "./events.mjs";
import { game } from "./game/index.mjs";
import { getExtendedRevenueEventProperties } from "./revenue.mjs";
import { TunePostbackTracker } from "./TunePostbackTracker.mjs";
const AMPLITUDE_NULL_VALUE = "(none)";
function createGuid() {
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, function(c) {
    const r = Math.random() * 16 | 0, v = c === "x" ? r : r & 3 | 8;
    return v.toString(16);
  });
}
class DummyTracker {
  setUserID(_userID) {
  }
  setUserProperties(_userProperties) {
  }
  track(_name, _data) {
  }
  trackRevenue(_opts) {
  }
  getTrackingQueryParams() {
    return {};
  }
  async flush() {
  }
}
class AmplitudeTracker {
  constructor(opts) {
    __publicField(this, "amplitudeClient");
    __publicField(this, "_userProperties", {});
    __publicField(this, "pendingRequests", /* @__PURE__ */ new Set());
    const { deviceID, key, name, version } = opts;
    this.amplitudeClient = amplitude.getInstance(name);
    const options = {
      // Always use HTTPS
      forceHttps: true,
      // Show less log spam
      logLevel: "ERROR",
      // Track referrer information
      includeReferrer: true,
      // Ensure referrer is tracked every time
      saveParamsReferrerOncePerSession: false,
      // Take the device ID from the `amp_device_id` query param if available
      deviceIdFromUrlParam: true
    };
    this.amplitudeClient.init(key, void 0, options);
    if (deviceID) {
      this.amplitudeClient.setDeviceId(deviceID);
    }
    this.amplitudeClient.setVersionName(version);
    this.setUserProperties(this._userProperties);
  }
  setUserID(userID) {
    this.amplitudeClient.setUserId(userID);
  }
  setDeviceID(deviceID) {
    this.amplitudeClient.setDeviceId(deviceID);
  }
  setUserProperties(newProperties) {
    for (const key in newProperties) {
      this._userProperties[key] = newProperties[key];
    }
    this.amplitudeClient.setUserProperties(this._userProperties);
  }
  track(name, data) {
    const pendingRequest = new Promise((resolve) => {
      function onComplete() {
        resolve();
      }
      this.amplitudeClient.logEventWithTimestamp(
        name,
        data,
        getAdjustedNow().now,
        onComplete,
        onComplete
      );
    });
    this.pendingRequests.add(pendingRequest);
    void pendingRequest.finally(() => {
      this.pendingRequests.delete(pendingRequest);
    });
  }
  async flush() {
    await Promise.all(this.pendingRequests);
  }
  trackRevenue(opts) {
    const {
      revenueType,
      revenueGross,
      revenueNet,
      productID,
      ...eventProperties
    } = opts;
    this.track("Revenue", opts);
    const amplitudeEvent = new amplitude.Revenue().setProductId(productID).setQuantity(1).setPrice(revenueNet).setRevenueType(revenueType).setEventProperties(eventProperties);
    this.amplitudeClient.logRevenueV2(amplitudeEvent);
  }
  getTrackingQueryParams() {
    return { amp_device_id: this.amplitudeClient.options.deviceId };
  }
}
class FBPixelTracker {
  constructor(osType) {
    __publicField(this, "_osType");
    this._osType = osType;
  }
  getTrackingQueryParams() {
    return {};
  }
  setUserID(_userID) {
  }
  setUserProperties(_newProperties) {
  }
  track(name, data) {
    window.fbq("trackCustom", name, { ...data, osType: this._osType });
  }
  trackRevenue(data) {
    window.fbq("track", "Purchase", {
      currency: "USD",
      value: data.price
    });
  }
  async flush() {
  }
}
class GoogleTagManagerTracker {
  getTrackingQueryParams() {
    return {};
  }
  setUserID(_userID) {
  }
  setUserProperties(_newProperties) {
  }
  track(name, data) {
    window.dataLayer.push(name);
  }
  trackRevenue(data) {
  }
  async flush() {
  }
}
class LineConversionApiTracker {
  constructor(userID, config) {
    __publicField(this, "lineConversionAPI");
    this.userID = userID;
    this.lineConversionAPI = new LineConversionAPI(config);
  }
  getTrackingQueryParams() {
    return {};
  }
  setUserID(userID) {
  }
  setUserProperties(newProperties) {
  }
  track(name, data) {
    this.lineConversionAPI.sendConversionEvent({
      eventType: name,
      userID: this.userID
    }).catch(() => {
    });
  }
  trackRevenue(opts) {
    this.lineConversionAPI.sendConversionEvent({
      eventType: "Purchase",
      userID: this.userID,
      revenue: {
        quantity: 1,
        currency: opts.currencyCodeLocal,
        category: [opts.revenueType],
        value: opts.revenueNet
      }
    }).catch(() => {
    });
  }
  async flush() {
  }
}
class PlatformTracker {
  constructor(platform) {
    __publicField(this, "_platform");
    this._platform = platform;
  }
  getTrackingQueryParams() {
    return {};
  }
  setUserID(userID) {
  }
  setUserProperties() {
  }
  track(name, data) {
    const properties = Object.keys(data);
    let last = properties.length;
    if (last > 25) {
      properties.sort();
      last = 25;
    }
    const restrictedData = {};
    for (let p = 0; p < last; p++) {
      const property = properties[p];
      restrictedData[property.replace(/[^a-z0-9_]/gi, "-")] = data[property];
    }
    this._platform.logEvent(name, 1, restrictedData);
  }
  trackRevenue() {
  }
  async flush() {
  }
}
class Analytics {
  constructor(opts) {
    /** Track common game events. */
    __publicField(this, "game", game);
    __publicField(this, "eventQueue", []);
    __publicField(this, "_gameVersion");
    __publicField(this, "processEventQueueTimeoutHandle", null);
    __publicField(this, "_userPropertiesBacklog");
    __publicField(this, "services");
    /**
     * Set analytics user properties.
     *
     * In case of user properties that are set on each entry use `Platform.sendEntryFinalAnalytics` instead of this method.
     *
     * @see https://docs.dev.gc-internal.net/gcinstant/#user-properties
     *
     * @param newProperties New user properties to set. See Amplitude documentation for the supported user property value types.
     * Make sure to **follow the naming conventions** documented in https://docs.dev.gc-internal.net/gcinstant/usage/#naming-conventions.
     * @param services A list of analytics services to use. Leave undefined to use the default service, i.e., Amplitude.
     *
     * @example
     * ```ts
     * analytics.setUserProperties({ attackCount: 2, score: 10 });
     * ```
     */
    __publicField(this, "setUserProperties");
    __publicField(this, "toAdServer");
    __publicField(this, "toDefault");
    __publicField(this, "toDefaultAndAdServer");
    __publicField(this, "toDefaultAndPixel");
    __publicField(this, "toDefaultAndPixelAndAdServer");
    __publicField(this, "_userProperties", {});
    __publicField(this, "config");
    __publicField(this, "initialized", false);
    __publicField(this, "trackedSessionStartEvents", false);
    __publicField(this, "zoomClientContext");
    this.opts = opts;
    this.services = {};
    this._userPropertiesBacklog = [];
    this.setUserProperties = (newProperties) => {
      this._userPropertiesBacklog.push(newProperties);
      for (const key in newProperties) {
        this._userProperties[key] = newProperties[key];
      }
    };
    document.addEventListener(
      "visibilitychange",
      () => trackVisibilityChange({
        visibilityState: document.visibilityState
      })
    );
  }
  setInternalUserProperties(newProperties, services) {
    this.setUserProperties(newProperties, services);
  }
  // TODO: accessing user properties through analytics is used either
  // a) internally in Platform to populate event payloads, or
  // b) in game code for game-specific analytics or hacks.
  //
  // In the future we could look into accessing userProperties through Platform,
  // i.e. making Platform the single source of truth for userProperties.
  /**
   * @returns The user properties set in the current session: this **does not include user properties set in previous sessions**.
   *
   * @see https://docs.dev.gc-internal.net/gcinstant/#user-properties
   */
  getUserProperties() {
    return this._userProperties;
  }
  addPlatformService(platform) {
    this.services.platform = new PlatformTracker(platform);
  }
  setZoomClientContext(clientContext) {
    this.zoomClientContext = clientContext;
  }
  /**
   * Initialize analytics.
   *
   * Analytics are initialized internally in GCInstant so there's no need to call this method from game code.
   *
   * @internal
   */
  initialize(config) {
    var _a;
    if (this.initialized) {
      return;
    }
    this.initialized = true;
    this._gameVersion = config.version;
    this.config = config;
    const commonUserProperties = {
      lastEntryGCInstantVersion: GCInstantVersion,
      lastEntryIsPageReload: pageWasReloaded(),
      version: this._gameVersion,
      usePolyfills: config.usePolyfills,
      lastEntryAppID: config.appID,
      sessionID: createGuid(),
      documentReferrer: userAgent.referrer,
      userAgent: userAgent.string,
      DEVICE_TYPE: userAgent.isMobile ? "mobile" : "desktop",
      OS_TYPE: userAgent.os.name,
      OS_VERSION: userAgent.os.version,
      BROWSER_TYPE: userAgent.browser.name,
      BROWSER_VERSION: userAgent.browser.version,
      APP_TYPE: userAgent.app.name,
      APP_VERSION: userAgent.app.version
    };
    if (config.amplitudeKey) {
      this.services.default = new AmplitudeTracker({
        key: config.amplitudeKey,
        name: "default",
        version: this._gameVersion
      });
    } else {
      this.services.default = new DummyTracker();
    }
    this.toDefault = [this.services.default];
    this.toDefaultAndAdServer = this.toDefault.slice();
    this.toDefaultAndPixel = this.toDefault.slice();
    this.toDefaultAndPixelAndAdServer = this.toDefault.slice();
    if (this.services.platform) {
      this.toDefault.push(this.services.platform);
    }
    if (config.amplitudeAdServerKey) {
      this.services.adServer = new AmplitudeTracker({
        key: config.amplitudeAdServerKey,
        name: "adServer",
        version: this._gameVersion
      });
    } else {
      this.services.adServer = new DummyTracker();
    }
    this.toAdServer = [this.services.adServer];
    this.toDefaultAndAdServer = this.toDefault.slice();
    this.toDefaultAndAdServer.push(this.services.adServer);
    this.toDefaultAndPixelAndAdServer = this.toDefaultAndAdServer.slice();
    if (config.fbPixelId) {
      this.services.pixel = new FBPixelTracker(userAgent.os.name);
      this.toDefaultAndPixel.push(this.services.pixel);
      this.toDefaultAndPixelAndAdServer.push(this.services.pixel);
    }
    if (config.googleTagManagerId) {
      this.services.gtmTracker = new GoogleTagManagerTracker();
      this.toDefaultAndPixel.push(this.services.gtmTracker);
      this.toDefaultAndPixelAndAdServer.push(this.services.gtmTracker);
    }
    const { transaction_id: tuneTransactionId } = getQueryParameters();
    if (config.tunePostbackEndpoint && tuneTransactionId) {
      const createTunePostbackTracker = ((_a = this.opts) == null ? void 0 : _a.createTunePostbackTracker) ?? ((opts) => new TunePostbackTracker(opts));
      this.services.tunePostback = createTunePostbackTracker({
        transactionId: tuneTransactionId,
        tuneEndpoint: config.tunePostbackEndpoint
      });
      this.toDefaultAndPixel.push(this.services.tunePostback);
      this.toDefaultAndPixelAndAdServer.push(this.services.tunePostback);
    }
    if (this._userPropertiesBacklog.length > 0) {
      for (const userProps of this._userPropertiesBacklog) {
        this.services.default.setUserProperties(userProps);
      }
    }
    this.setUserProperties = (newProperties, services = this.toDefault || []) => {
      if (config.isDevelopment) {
        console.log("analytics.setUserProperties:", newProperties);
      }
      for (const service of services) {
        service.setUserProperties(newProperties);
      }
      for (const key in newProperties) {
        this._userProperties[key] = newProperties[key];
      }
    };
    this.setInternalUserProperties(commonUserProperties);
    this.setInternalUserProperties(
      { lastEntryVersion: this._gameVersion, gameID: config.shortName },
      this.toAdServer
    );
    this.pushEvent("InitAnalyticsSuccess");
  }
  /** @internal */
  setUserID(userID) {
    if (!this.initialized) {
      throw Error("Cannot set user ID before analytics initialization");
    }
    if (this.config.lineConversionAPIConfig && !this.services.lineConversionApi) {
      this.services.lineConversionApi = new LineConversionApiTracker(
        userID,
        this.config
      );
      this.toDefaultAndPixel.push(this.services.lineConversionApi);
      this.toDefaultAndPixelAndAdServer.push(this.services.lineConversionApi);
    }
    for (const service of Object.values(this.services)) {
      service.setUserID(userID);
    }
    this.setInternalUserProperties({ playerID: userID });
    if (this.services.adServer) {
      this.services.adServer.setUserProperties({
        lastEntrySourceGamePlayerID: userID
      });
    }
  }
  setAmplitudeDeviceID(deviceID) {
    if (!this.initialized) {
      throw Error("Cannot set device ID before analytics initialization");
    }
    if (this.services.default instanceof AmplitudeTracker) {
      this.services.default.setDeviceID(deviceID);
    }
    if (this.services.adServer instanceof AmplitudeTracker) {
      this.services.adServer.setDeviceID(deviceID);
    }
  }
  /** @internal */
  trackSessionStartEvents() {
    if (!this.initialized) {
      throw Error("Cannot start session before analytics initialization");
    }
    if (this.trackedSessionStartEvents) {
      return;
    }
    this.trackedSessionStartEvents = true;
    const sessionEventProps = {};
    const cdnEntryTimestamp = Number(getQueryParameters().cdn_ts);
    if (cdnEntryTimestamp) {
      sessionEventProps.elapsedSinceCDNEntry = globalTime.getNow() - cdnEntryTimestamp;
    }
    this.pushEvent("session", sessionEventProps);
    try {
      if (!localStorage.getItem("device-install")) {
        localStorage.setItem("device-install", "true");
        this.pushEvent("device-install", null, this.toDefaultAndPixel);
      }
    } catch (error) {
      console.error("Failed to handle device-install event push", error);
    }
  }
  /**
   * Track an analytics event.
   *
   * @see https://docs.dev.gc-internal.net/gcinstant/#events
   *
   * @param eventName Name of the analytics event. Use **UpperCamelCase** for naming.
   * @param eventProperties Event properties to attach to the event. See Amplitude documentation for the supported event property value types.
   * Make sure to **follow the naming conventions** documented in https://docs.dev.gc-internal.net/gcinstant/usage/#naming-conventions.
   * @param services A list of analytics services to use. Leave undefined to use the default service, i.e., Amplitude.
   * @param newUserProperties New user properties to set along with the event. Use `analytics.setUserProperties` to set user properties without tracking an event.
   *
   * @example
   * ```ts
   * analytics.pushEvent('LevelUp', { newLevel: 2 }); // Track an event
   *
   * analytics.pushEvent('LevelUp', {}, undefined, { #realtimeLevel: 2 }); // Track an event and set a user property
   * ```
   */
  pushEvent(eventName, eventProperties, services, newUserProperties) {
    var _a;
    const curatedData = this.getDefaultEventProperties();
    for (const key in eventProperties) {
      if (eventProperties[key] !== null && eventProperties[key] !== void 0) {
        curatedData[key] = eventProperties[key];
      }
    }
    if ((_a = this.config) == null ? void 0 : _a.isDevelopment) {
      if (curatedData.errorMessage === void 0) {
        console.log(`analytics.pushEvent: ${eventName} =`, curatedData);
      } else {
        console.log(`analytics.pushError: ${eventName} =`, curatedData);
      }
    }
    this.handleZoomUserProperties();
    this.eventQueue.push({
      eventName,
      eventProperties: curatedData,
      services,
      userProperties: newUserProperties
    });
    this.processEventQueue();
  }
  getDefaultEventProperties() {
    return {
      sessionElapsedTime: Math.round(window.performance.now()) / 1e3,
      timestamp: new Date(getAdjustedNow().now).toISOString(),
      version: this._gameVersion
    };
  }
  /**
   * Zoom supports two client contexts one when in meeting and one in main window.
   *
   * Set client context properties that do not belong to the current context to none.
   *
   * Reset client context properties that do belong to the current context to value from user properties.
   *
   * This is needed because amplitude uses the user properties from previous events irrespective of the context.
   * So all the client context properties need to be set again.
   */
  handleZoomUserProperties() {
    var _a, _b, _c, _d;
    if (!this.zoomClientContext) {
      return;
    }
    let setToNoneProps;
    let setToValueProps;
    if (this.zoomClientContext === "inMeeting") {
      setToNoneProps = (_a = this.config) == null ? void 0 : _a.zoomMainClientUserProperties;
      setToValueProps = (_b = this.config) == null ? void 0 : _b.zoomMeetingClientUserProperties;
    } else if (this.zoomClientContext === "inMainClient") {
      setToNoneProps = (_c = this.config) == null ? void 0 : _c.zoomMeetingClientUserProperties;
      setToValueProps = (_d = this.config) == null ? void 0 : _d.zoomMainClientUserProperties;
    }
    const userPropsToSet = {
      clientContext: this.zoomClientContext
    };
    if (setToNoneProps) {
      for (const key of setToNoneProps) {
        userPropsToSet[key] = AMPLITUDE_NULL_VALUE;
      }
    }
    const userProps = this.getUserProperties();
    if (setToValueProps) {
      for (const key of setToValueProps) {
        const val = userProps[key];
        if (val) {
          userPropsToSet[key] = userProps[key];
        }
      }
    }
    this.setInternalUserProperties(userPropsToSet);
  }
  pushError(name, error, data, services) {
    var _a;
    data = data || {};
    error = error || {};
    if ((_a = this.config) == null ? void 0 : _a.isDevelopment) {
      console.error("Sending ERROR:", name, error, data);
    }
    this.pushEvent(
      name,
      {
        ...data,
        errorCode: error.code || "No Code",
        errorMessage: error.message || "No Message",
        errorStack: error.stack || "No Stack"
      },
      services
    );
    this.pushEvent(
      "Error",
      {
        ...data,
        errorOrigin: name,
        errorCode: error.code || "No Code",
        errorMessage: error.message || "No Message",
        errorStack: error.stack || "No Stack"
      },
      services
    );
  }
  pushRevenue(revenueOpts) {
    if (!this.config) {
      throw Error("Initialize analytics before tracking revenue");
    }
    const opts = { ...revenueOpts };
    if (opts.revenueType === "in_app_purchase" && opts.revenueProvider) {
      opts.purchaseProvider = opts.revenueProvider;
    }
    const eventProps = {
      ...this.getDefaultEventProperties(),
      ...opts,
      ...getExtendedRevenueEventProperties(opts, this.config)
    };
    delete eventProps.purchaseProviderCreditCardBrand;
    this.services.default.trackRevenue(eventProps);
    if (this.config.fbPixelId) {
      this.services.pixel.track("Revenue", {
        ...opts,
        currency: "USD",
        value: eventProps.revenueNet
      });
    }
    if (this.services.lineConversionApi) {
      this.services.lineConversionApi.trackRevenue(eventProps);
    }
    if (this.services.tunePostback) {
      this.services.tunePostback.trackRevenue(eventProps);
    }
    this.processEventQueue();
  }
  /**
   * Process all pending events
   */
  processEventQueue() {
    if (!this.initialized) {
      if (this.processEventQueueTimeoutHandle === null) {
        this.processEventQueueTimeoutHandle = window.setTimeout(() => {
          this.processEventQueue();
        }, 1e3);
      }
      return;
    }
    for (const queuedEvent of this.eventQueue) {
      const { eventName, eventProperties, userProperties } = queuedEvent;
      const services = queuedEvent.services ?? this.toDefault;
      if (userProperties && Object.keys(userProperties).length > 0) {
        this.setUserProperties(userProperties, services);
      }
      for (const service of services) {
        service.track(eventName, eventProperties);
      }
    }
    this.eventQueue.length = 0;
  }
  /**
   * Returns the search query parameters that can be used for cross-domain tracking. Will be empty when not using Amplitude.
   */
  getTrackingQueryParams() {
    var _a;
    return ((_a = this.services.default) == null ? void 0 : _a.getTrackingQueryParams()) ?? {};
  }
  /**
   * Waits for outgoing events to finish processing.
   *
   * You probably should not use this API, events are automatically flushed. Call
   * `await analytics.flush()` before redirecting or reloading the page to wait for any pending
   * events to finish sending. Note that user property requests are not currently flushed due to
   * Amplitude SDK limitations.
   */
  async flush() {
    await Promise.all(this.toDefault.map((service) => service.flush()));
  }
}
const analytics = new Analytics();
const internalAnalytics = analytics;
const analytics$1 = analytics;
const originalOnError = window.onerror;
const sentErrors = {};
window.onerror = (msg, url, line, col, error) => {
  const errorID = line + "." + col;
  if (sentErrors[errorID]) {
    return;
  }
  sentErrors[errorID] = true;
  analytics.pushError("window.onerror", error, {
    msg,
    url,
    line,
    col
  });
  originalOnError == null ? void 0 : originalOnError(msg, url, line, col, error);
};
window.addEventListener("unhandledrejection", function(rejection) {
  var _a;
  const error = rejection.reason || ((_a = rejection.detail) == null ? void 0 : _a.reason);
  if ((error == null ? void 0 : error.code) === "NETWORK_FAILURE" && (error == null ? void 0 : error.message) === "A network error has occurred") {
    console.warn("Unhandled rejection due to network failure:", error);
    return;
  }
  analytics.pushError("PromiseError", error);
});
function getAdjustedNow() {
  const clientTime = Date.now();
  const serverTime = globalTime.getNow();
  const clockDrift = Math.abs(serverTime - clientTime);
  const clientIsDrifting = clockDrift > duration({ minutes: 1 });
  return clientIsDrifting ? { now: serverTime, timestampIsFromServer: true } : { now: clientTime, timestampIsFromServer: false };
}
export {
  AMPLITUDE_NULL_VALUE,
  Analytics,
  analytics,
  analytics$1 as default,
  getAdjustedNow,
  internalAnalytics
};
